import React, { useMemo } from 'react';
import { motion } from 'framer-motion';

export type FluidTabItem = {
  id: string;
  label: string;
  icon?: React.ReactNode;
};

type FluidTabsProps = {
  tabs: FluidTabItem[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  className?: string;
  /** Replaces default `font-felix-titling` on tab buttons (e.g. landing menu Petit Formal) */
  tabFontClassName?: string;
};

export const FluidTabs: React.FC<FluidTabsProps> = ({
  tabs,
  activeTab,
  onTabChange,
  className = '',
  tabFontClassName = 'font-felix-titling text-sm',
}) => {
  const n = tabs.length;
  const activeIndex = useMemo(() => {
    const i = tabs.findIndex((tab) => tab.id === activeTab);
    return i >= 0 ? i : 0;
  }, [tabs, activeTab]);

  if (n === 0) return null;

  return (
    <div className={`flex items-center justify-center py-0 sm:py-4 ${className}`}>
      <div
        className="relative flex w-full max-w-2xl overflow-hidden rounded-full bg-[#f5f1eb] p-0.5 shadow-lg sm:p-1"
        role="tablist"
        aria-label="Tabs"
      >
        {/* Sliding pill: width matches each flex-1 segment; x moves by one segment per index (no flex gaps). */}
        <motion.div
          className="pointer-events-none absolute left-0 top-1 bottom-1 z-0 rounded-full bg-white shadow-sm"
          initial={false}
          animate={{ x: `${activeIndex * 100}%` }}
          transition={{ type: 'spring', stiffness: 380, damping: 34 }}
          style={{ width: `${100 / n}%` }}
        />
        {tabs.map((tab) => {
          const selected = activeTab === tab.id;
          return (
            <motion.button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={selected}
              title={tab.label}
              className={`${tabFontClassName} relative z-10 flex min-w-0 flex-1 items-center justify-center gap-1.5 px-2 py-2.5 font-normal uppercase tracking-wide transition-colors duration-300 sm:px-4 sm:py-3 ${
                selected ? 'text-black' : 'text-gray-500'
              }`}
              whileHover={{ y: -1 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onTabChange(tab.id)}
            >
              {tab.icon}
              <span className="min-w-0 truncate">{tab.label}</span>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
};
